import { signal } from '@preact/signals';

import type {
  HouseholdTypeValue,
  ModuleIdValue,
  ModulesResponse,
  ModuleView,
} from '@amumin/api-client';
import { isLargeTextType, isModuleId, presetOf } from '@amumin/domain/modules';

import { api } from '../api.js';

// Modules van het gezin in de ouderapp (0029_modules; docs/DESIGN.md §8.M). De server is de bron:
// GET /v1/app/modules na het laden van de schil en na elke wijziging. Zolang de lijst er niet is
// (of een oudere server hem niet kent), staat alles aan: niets verdwijnt door een trage verbinding.

export const modules = signal<ModulesResponse | null>(null);
/** De eerste poging om te laden is klaar (gelukt of niet); schermen van een module wachten erop. */
export const modulesReady = signal(false);
/**
 * Golf 6b: de soort huishouden zolang de lijst er nog niet is (householdType.ts zet hem). Dan volgt
 * "aan" de standaard van de soort, zodat bv. Beloningen bij huisgenoten nooit even opflitst.
 */
export const fallbackType = signal<HouseholdTypeValue | null>(null);

/** Elke keuze (ook optimistisch) telt op; een oud leesantwoord mag een nieuwere keuze nooit overschrijven. */
let generation = 0;

/** Leest de modules; het antwoord telt alleen als er intussen niets gekozen is. */
export async function fetchModules(): Promise<ModulesResponse> {
  const gen = generation;
  const res = await api.app.modules.list();
  if (gen === generation) modules.value = res;
  return modules.value ?? res;
}

export async function loadModules(): Promise<void> {
  try {
    await fetchModules();
  } catch {
    // stil: zonder lijst geldt "alles aan" (de server bewaakt het echte aan/uit)
  } finally {
    modulesReady.value = true;
  }
}

export function setModules(res: ModulesResponse): void {
  generation += 1;
  modules.value = res;
}

function find(id: ModuleIdValue): ModuleView | undefined {
  return modules.value?.items.find((m) => m.id === id);
}

/** 0032: modules die standaard uit staan (gevoelig); die tonen we pas als de server "aan" zegt. */
// Golf 7 (0048): Soera's leren bestaat alleen als de server hem noemt (Islam en Koranles).
// Golf 8 (0049): Samen wonen staat standaard uit (de soort zet hem aan; zie presetOf hieronder).
// Golf 8 (0050): Check-in en Kring staan alleen aan met een expliciete keuze (en CHECK_IN_ENABLED).
const OPT_IN: ReadonlyArray<ModuleIdValue> = [
  'health',
  'school',
  'memorize',
  'dinner_list',
  'expenses',
  'bookings',
  'check_in',
  'family_circle',
  // 0064: Fotolijst (standaard uit: zonder foto's doet hij niets).
  'photo_frame',
  // 0068: Gezond op gewicht (standaard uit; persoonlijk, gezondheid).
  'kompas',
];

/** Modules die de API alleen aan volwassenen noemt (registry parent_only). */
const PARENT_ONLY: ReadonlyArray<ModuleIdValue> = ['expenses', 'photo_frame', 'kompas', 'money'];

/**
 * Modules die alleen bestaan als het gezin erbij hoort (Islam en Koranles): noemt de geladen lijst
 * ze niet, dan bestaat het scherm voor dit gezin niet (NotFound, geen 'staat uit'). Gebedstijden
 * hoeft hier niet: /instellingen/gezin/extra toont zonder module zelf al 'niet gevonden'.
 */
// Golf 8 (0050): Check-in en Kring bestaan niet zolang CHECK_IN_ENABLED uit staat.
const OFFERED_ONLY: ReadonlyArray<ModuleIdValue> = ['memorize', 'check_in', 'family_circle'];

/** Bestaat deze module niet voor dit gezin (lijst geladen en hij staat er niet in)? */
export function moduleUnlisted(id: ModuleIdValue): boolean {
  if (OFFERED_ONLY.indexOf(id) < 0 || !modules.value) return false;
  return !find(id);
}

/**
 * Golf 8: hoort "Check-in en kring" bij dit huishouden? Alleen als de server de module noemt
 * (vlag aan) en hij niet verborgen is voor deze soort (Senior en Zorg), of hij toch al aan staat.
 */
export function careOffered(): boolean {
  const m = find('check_in');
  return !!m && (m.hidden !== true || m.on);
}

/**
 * Golf 8 live r1: Senior of Zorg terwijl CHECK_IN_ENABLED uit staat (launch gate). De server noemt
 * de check-in dan niet; de app zegt rustig dat hij binnenkort komt, in plaats van niets.
 */
export function careComing(): boolean {
  const type = fallbackType.value;
  return !!modules.value && !find('check_in') && !!type && isLargeTextType(type);
}

/** Staat de module aan? Onbekend of nog niet geladen: aan (behalve de opt-in-modules: uit). */
export function moduleOn(id: ModuleIdValue): boolean {
  const m = find(id);
  if (m) return m.on;
  // Golf 8: Kosten delen is alleen voor volwassenen; noemt de geladen lijst hem niet (een kind),
  // dan bestaat hij voor deze login niet.
  if (modules.value && PARENT_ONLY.indexOf(id) >= 0) return false;
  const type = fallbackType.value;
  if (type && isModuleId(id)) {
    const preset = presetOf(type, id);
    if (preset) return preset.on;
  }
  return OPT_IN.indexOf(id) < 0 && id !== 'chore_rotation';
}

/**
 * Werkt een optie van een module? Alleen als de module aan staat; zonder lijst de standaard
 * (`fallback`). Voor `rides.travel_blocks` moet ook Reistijd aan staan.
 */
export function moduleOption(id: ModuleIdValue, key: string, fallback: boolean): boolean {
  const m = find(id);
  if (!m) return fallback;
  if (!m.on) return false;
  const v = m.options[key];
  return typeof v === 'boolean' ? v : fallback;
}

/** Brengen en halen in de agendaweergaven (dag, week, maand, lijst, Vandaag). */
export function ridesInAgenda(): boolean {
  return moduleOption('rides', 'show_in_agenda', true);
}

/** Reistijd als eigen blokje vóór de afspraak (dag en week). */
export function travelBlocks(): boolean {
  return moduleOption('rides', 'travel_blocks', false) && moduleOn('travel_time');
}
